/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable import/no-default-export */
import React, { useRef } from 'react';
import { Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/table';
import {
  Flex,
  Box,
  Text,
  ScaleFade,
  Alert,
  AlertIcon,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  chakra,
  MenuItem,
} from '@chakra-ui/core';
import { css } from '@emotion/core';
import { useTable, usePagination, Cell, Column, useSortBy } from 'react-table';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { MdOutlineUnfoldMore } from 'react-icons/md';
import colors from '../../../styles/colors';
import { FitbandTemplateType } from './FitbandConfigurationTypes';
import { mainTable, tableHeading } from '../recruiterManagement/recruiterDashboard/recruitersInfoTable/RecruiterInfoTableStyle';
import { getConsistentDateFormat } from '../../../utils/dateUtils';

export const TableStyle = css`
  cursor: pointer;
  &:hover, &:focus, &:active {
    background: #f7fafc;
  }
  &.activeRow {
    background: ${colors.gray[50]};
  }
  &:hover .rightArrow {
    opacity: 1;
    transform: translate(0, -50%);
   }
}
`;

export type TableProps<Data extends Record<string, unknown>> = {
  data: Data[];
  columns: Column<Data>[];
  isLoading?: boolean;
  handleCopy: (data: FitbandTemplateType) => void;
  handleEdit: (id: string) => void;
  handleView: (id: string) => void;
};

export default function FitbandConfigurationTableView<Data extends Record<string, unknown>>({
  data,
  columns,
  isLoading,
  handleCopy,
  handleEdit,
  handleView,
}: TableProps<Data>) {
  const { t } = useTranslation('administration');
  const tableContainerRef = useRef<null | HTMLTableElement>(null);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy,
    usePagination,
  );

  const renderCell = (cellData: Cell<Data>) => {
    const columnData = (cellData.row.original as unknown) as FitbandTemplateType;

    switch (cellData.column.id) {
      case 'actionType':
        return (
          <Menu key={uuidv4()}>
            <MenuButton
              height="32px"
              px={4}
              py={2}
              transition="all 0.2s"
              borderRadius="md"
              borderWidth="1px"
              border="none"
              _hover={{ bg: colors.lavenderMist }}
              data-testid={`MenuButton-${columnData.id}`}
              key={uuidv4()}
            >
              <BsThreeDotsVertical />
            </MenuButton>
            <MenuList minWidth="8rem" key={uuidv4()}>
              <MenuItem onClick={/* istanbul ignore next */ () => handleCopy(columnData)}>
                {t('fitbandConfiguration.duplicate')}
              </MenuItem>
              {!columnData?.default && (
                <MenuItem onClick={/* istanbul ignore next */ () => handleEdit(columnData?.id)}>
                  {t('fitbandConfiguration.edit')}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        );
      case 'name':
        return (
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            onClick={/* istanbul ignore next */ () => handleView(columnData.id)}
            _hover={{ textDecoration: 'underline' }}
          >
            <Text fontSize="14px" data-testid="templateNameCell">
              {columnData.name}
            </Text>
            {columnData?.default && (
              <Text fontSize="14px" color={colors.gray[400]} fontStyle="italic" ml={2}>
                {t('fitbandConfiguration.systemDefault')}
              </Text>
            )}
          </Box>
        );
      case 'createdAt':
        return (
          <Box>
            <Text fontSize="14px">{getConsistentDateFormat(columnData.createdAt, 'full')}</Text>
          </Box>
        );
      case 'modifiedAt':
        return (
          <Box>
            {columnData.modifiedAt ? (
              <Text fontSize="14px">{getConsistentDateFormat(columnData.modifiedAt, 'full')}</Text>
            ) : (
              <Text fontSize="14px" fontStyle="italic" color={colors.gray[400]}>
                {t('fitbandConfiguration.noChanges')}
              </Text>
            )}
          </Box>
        );
      /* istanbul ignore next */
      default:
        return <Text>{cellData.value !== undefined ? cellData.render('Cell') : 'N/A'}</Text>;
    }
  };

  return (
    <ScaleFade initialScale={0.7} in unmountOnExit={false}>
      <Table
        {...getTableProps()}
        className="mainTable simple-bordered-table"
        key="mainTable"
        css={mainTable}
        // w="100%"
        // border="1px solid #E5E7EB"
        // borderCollapse="collapse"
        // bg="#fff"
        ref={tableContainerRef}
      >
        <Thead css={tableHeading} className="tableHeading">
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()} textAlign="left" key={headerGroup.getHeaderGroupProps().key}>
              {headerGroup.headers.map((column) => {
                return (
                  <Th
                    {...omit(column.getHeaderProps(column.getSortByToggleProps()), 'title')}
                    isNumeric={column.isNumeric}
                    width={column.width}
                    key={column.getHeaderProps().key}
                    // className="fixBorder"
                    // px={5}
                    // py={3}
                    // w={column.id === 'actionType' ? '20%' : 'auto'}
                    align="left"
                  >
                    <Box display="flex" width="fit-content">
                      {column.render('Header')}
                      {!column?.disableSortBy && (
                        <chakra.span pl="4">
                          {column.isSorted ? (
                            // istanbul ignore next

                            column.isSortedDesc ? (
                              <IoChevronDown aria-label="sorted descending" />
                            ) : (
                              <IoChevronUp aria-label="sorted ascending" />
                            )
                          ) : (
                            <MdOutlineUnfoldMore aria-label="unsorted" />
                          )}
                        </chakra.span>
                      )}
                    </Box>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {!isLoading ? (
            data.length ? (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <Tr
                    css={TableStyle}
                    {...row.getRowProps()}
                    style={{
                      opacity: isLoading ? /* istanbul ignore next */ 0.5 : 1,
                      pointerEvents: isLoading ? /* istanbul ignore next */ 'none' : 'all',
                    }}
                    key={row.getRowProps().key}
                    cursor="pointer"
                  >
                    {row.cells.map((cell) => (
                      <Td
                        {...cell.getCellProps()}
                        sNumeric={cell.column.isNumeric}
                        // border="1px solid #E5E7EB"
                        key={cell.getCellProps().key}
                        px={5}
                        py={3}
                      >
                        {renderCell(cell)}
                      </Td>
                    ))}
                  </Tr>
                );
              })
            ) : (
              <Td colSpan={4}>
                <Alert status="info" backgroundColor={colors.gray[400]} color={colors.white}>
                  <AlertIcon color={colors.white} />
                  {t('fitbandConfiguration.noDataFound')}
                </Alert>
              </Td>
            )
          ) : (
            <Td colSpan={4}>
              <Flex alignItems="center" justifyContent="center" p={5}>
                <Spinner colorScheme="blue" size="lg" />
              </Flex>
            </Td>
          )}
        </Tbody>
      </Table>
    </ScaleFade>
  );
}
